import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import axios from 'axios';
import { Checkbox } from 'components/Checkbox';
import { TokenLogo } from 'components/TokenLogo/TokenLogo';
import { CHAINS, SCAN_NAMES, WOWMAX_API_URL } from 'consts';
import { store } from 'store';
import { TokenInfo } from 'types';
import { bn } from 'utils/BigNumber';
import { cutTokenName, cutTokenSymbol, getScanUrl } from 'utils/helpers';
import { Address, erc20Abi } from 'viem';
import { useReadContract } from 'wagmi';

import * as S from './TokenSearch.styles';

const GECKO_ID: Record<number, string> = {
  [CHAINS.ethereum.id]: 'eth',
  [CHAINS.bsc.id]: 'bsc',
  [CHAINS.cronos.id]: 'cro',
  [CHAINS.base.id]: 'base',
  [CHAINS.linea.id]: 'linea',
  [CHAINS.scroll.id]: 'scroll',
  [CHAINS.manta.id]: 'manta-pacific',
  [CHAINS.blast.id]: 'blast',
  [CHAINS.metis.id]: 'metis',
  [CHAINS.arbitrum.id]: 'arbitrum',
  [CHAINS.zetachain.id]: 'zetachain',
  [CHAINS.soneium.id]: 'soneium',
  [CHAINS.hyper.id]: 'hyperevm',
  [CHAINS.zeroG.id]: '0g',
};

const RISK_CHAIN_ID: Record<number, string> = {
  [CHAINS.ethereum.id]: 'eth',
  [CHAINS.bsc.id]: 'bsc',
  [CHAINS.base.id]: 'base',
  [CHAINS.arbitrum.id]: 'arb',
};

const COLOR_BY_RISK = {
  'Low Risk': 'green',
  'Medium Risk': 'yellow',
  'High Risk': 'red',
} as const;

type RiskScore = keyof typeof COLOR_BY_RISK;

const getRiskScore = (overallRisk: number): RiskScore => {
  if (overallRisk <= 23) return 'Low Risk';
  if (overallRisk <= 50) return 'Medium Risk';
  return 'High Risk';
};

type Tag = {
  name: string;
  description: string;
  type: string;
  severity: number;
  key: string;
};

type RiskResponse = {
  overallRisk: number;
  issues?: {
    riskScore: RiskScore;
    tags?: Tag[];
  }[];
};

type ResponseToken = {
  address: Address;
  symbol: string;
  name: string;
  decimals: number;
  priceUsd: number;
  reservesUsd: number;
};

interface TokenSearchProps {
  address: Address | null;
  onImport: (token: Address) => void;
}

export const TokenSearch = observer(({ address, onImport }: TokenSearchProps) => {
  const { chainId, account, addBalances } = store.web3Store;
  const { addCustomToken } = store.commonStore;

  const [isAccepted, setIsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState<TokenInfo | null>(null);
  const [risk, setRisk] = useState<RiskResponse | null>(null);
  const [isIssuesOpen, setIsIssuesOpen] = useState(false);

  const riskScore = risk ? getRiskScore(risk.overallRisk) : null;
  const riskColor = riskScore ? COLOR_BY_RISK[riskScore] : 'text';
  const issues: Tag[] = [];
  risk?.issues?.forEach((issue) => {
    issues.push(...(issue.tags || []));
  });

  const fetchRisk = async (address: Address) => {
    try {
      const chain = RISK_CHAIN_ID[chainId];
      const url = chain
        ? `${WOWMAX_API_URL}/threat-consideration/chains/${chain}/addresses/${address}`
        : `${WOWMAX_API_URL}/threat-consideration/addresses/${address}`;
      const response = await axios.get<RiskResponse>(url);
      setRisk(response.data);
    } catch (err) {
      console.log('webacy error', err);
    }
  };

  useEffect(() => {
    setRisk(null);
    setIsIssuesOpen(false);

    (async () => {
      if (!address) return;
      setToken(null);
      setIsLoading(true);
      let token: TokenInfo | null = null;

      try {
        const response = await axios.get<ResponseToken[]>(
          `${WOWMAX_API_URL}/chains/${chainId}/tokens/${address}`
        );
        if (response.data.length !== 0) {
          const t = response.data[0];
          token = {
            address: address.toLowerCase() as Address,
            symbol: t.symbol,
            name: t.name,
            decimals: t.decimals,
            chainId,
            logoURI: '',
            tags: ['custom'],
          };
        }
      } catch (err) {
        console.log('fetch custom token error', err);
      }

      if (token && GECKO_ID[chainId]) {
        try {
          const response = await axios.get(
            `https://api.geckoterminal.com/api/v2/networks/${GECKO_ID[chainId]}/tokens/${token.address}`
          );
          token.logoURI = response.data?.data?.attributes.image_url || '';
        } catch (err) {
          console.log('fetch custom token gecko error', err);
        }
      }

      if (token) {
        fetchRisk(token.address);
      }

      setToken(token);
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, chainId]);

  const balanceResult = useReadContract(
    token && account
      ? {
          address: token.address,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [account],
          chainId,
        }
      : undefined
  );

  const balance = balanceResult.isSuccess ? balanceResult.data.toString() : null;

  const handleImportClick = () => {
    if (!token) return;
    addCustomToken(token);
    addBalances(chainId, { [token.address]: balance || undefined });
    onImport(token.address);
  };

  if (isLoading) return <S.Message>...Loading</S.Message>;

  if (!isLoading && !token) return <S.Message>Unavailable token</S.Message>;

  if (!token) return null;

  return (
    <S.Root>
      <S.Token>
        <TokenLogo token={token} size={32} />
        <S.TokenInfo>
          <S.TokenName>{cutTokenName(token.name)}</S.TokenName>
          <S.TokenAmount>
            {balance && bn(balance).toView(token.decimals)} {cutTokenSymbol(token.symbol, 20)}
          </S.TokenAmount>
        </S.TokenInfo>
      </S.Token>
      {risk && (
        <S.Risk>
          <S.RiskMain>
            <S.RiskLeft>
              <S.RiskLeftContent>
                <S.RiskValue $color={riskColor}>{riskScore}</S.RiskValue>
                <S.Bar $percent={risk.overallRisk} $color={riskColor} />
              </S.RiskLeftContent>
            </S.RiskLeft>
            <S.RiskRight>
              <S.RiskLogo>
                <span>Powered by</span>
                <a href="https://www.webacy.com/" target="_blank" rel="noreferrer noopener">
                  DD.xyz
                </a>
              </S.RiskLogo>
              <S.RiskButtons>
                {!!issues.length && (
                  <S.IssuesButton
                    $isOpen={isIssuesOpen}
                    onClick={() => setIsIssuesOpen(!isIssuesOpen)}
                  >
                    {issues.length} Issues
                  </S.IssuesButton>
                )}
                <S.RiskLink
                  href={`https://dapp.webacy.com/dyor/address/${address}?chain=${
                    RISK_CHAIN_ID[chainId] || 'eth'
                  }`}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Full report ↗
                </S.RiskLink>
              </S.RiskButtons>
            </S.RiskRight>
          </S.RiskMain>
          <S.Issues hidden={!isIssuesOpen || !issues.length}>
            {issues.map((el, i) => (
              <S.Issue key={i}>
                <S.IssueHeader>
                  <S.IssueCaption>{el.name}</S.IssueCaption>
                  <S.IssueSeverity>Severity: {el.severity}</S.IssueSeverity>
                </S.IssueHeader>
                <S.IssueText>{el.description}</S.IssueText>
              </S.Issue>
            ))}
          </S.Issues>
        </S.Risk>
      )}
      <S.WarningWrapper>
        <S.WarningContent>
          <S.WarningText>
            Make sure you have checked the token address and it is correct
          </S.WarningText>
          <S.ScanLink
            href={getScanUrl(`/token/${address}`, chainId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            View on {SCAN_NAMES[chainId]} ↗
          </S.ScanLink>
        </S.WarningContent>
      </S.WarningWrapper>
      <S.Text>
        Anyone can create any token, including fake versions of the existing tokens. Take due care.
        Some tokens and their technical parameters may be incompatible with WOWMAX services. By
        importing this custom token you acknowledge and accept the risks.
      </S.Text>
      <S.Accept>
        <Checkbox checked={isAccepted} onChange={() => setIsAccepted(!isAccepted)} text="Accept" />
      </S.Accept>
      <S.ImportButton disabled={!isAccepted} onClick={handleImportClick}>
        Import
      </S.ImportButton>
    </S.Root>
  );
});
